import metricMetadataIcon from '../../assets/general/metric-metadata-icon-credc.svg';
import { useState } from "react";
import MetricMetadataModal from "../modals/MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../../utils/dashboardUtils";

interface SimpleLabelProps {
    id: number;
    title: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: React.FC<React.SVGProps<SVGSVGElement>>;
    showMetadataIcon?: boolean;
};

const SimpleLabel = ({ id, title, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: SimpleLabelProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }
        
        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="flex items-baseline justify-start space-x-2">
                <p className="text-port-text text-xs font-bold">{title}</p>
                {showMetadataIcon && 
                    <div className="flex-shrink-0 h-4 w-3 mb-2 group relative">
                        <button onClick={handleClick} title="View more information">
                            <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                        </button>
                    </div>
                }
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                disaggData={disaggData}
                stateData={null}
                nationalData={null}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                geoTag={geoTag}
                svg={svg}
                loading={loading}
                error={error}
            />
        </>
    );
};


export default SimpleLabel;
